<script setup>
import axios from 'axios';
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { finishActivation } from '../../../../helpers/activation';
import Loading from '../../../Loading.vue';
import PgeButton from '../../../../pge-components/pge-button.vue';
import PgeInputText from '../../../../pge-components/pge-input-text.vue';
import { useAuthStore } from '../../../../stores/auth';
import { useEventStore } from '../../../../stores/event';
import FormDropdownGroup from './../../../../pge-components/pge-dropdown/pge-dropdown-group.vue';
import FormDropdown from './../../../../pge-components/pge-dropdown/pge-dropdown.vue';
import { published } from '../../../../helpers/policyLines';
import { stashRegistration } from '../../../../helpers/pendingRegistration';

const router = useRouter();
const authStore = useAuthStore();
const { event } = useEventStore();

const props = defineProps({
	eventData: Object,
	changeFormStep: Function,
	currentFormStepData: {
		type: Object,
		default: () => ({}),
	},
	// true → existing account joining this hub (attach + auto-login)
	// false → brand-new account (register + email verification)
	emailExist: {
		type: Boolean,
		default: false,
	},
});

const isLoading = ref(false);
const allowSubmit = ref(false);
const errors = ref([]);
const turnstileToken = ref(false);

const user = props.currentFormStepData.user || {};
const registerGroups = ref(props.eventData.settings.fields || []);
const requiredFields = ref(
	props.eventData.settings.fields
		? props.eventData.settings.fields
			.map(({ fields }) => fields.filter(field => field.required))
			.filter(item => item.length)
		: []
);
const userFormData = ref({});

// The answer is stored as the line's id (XN-4538), so a rename reaches it. The
// payload carries archived lines as well — for resolving an old answer on a
// profile — and those are not offered to somebody registering now.
const policyLineOptions = computed(() => published(props.eventData.policy_lines ?? [])
	.map(line => ({ label: line.name, value: String(line.id) })));
const hasFields = computed(() => registerGroups.value.length > 0);

function collectRegistrationFields() {
	const payload = {};
	Object.keys(userFormData.value).forEach(key => {
		if (key.startsWith('reg_')) payload[key] = userFormData.value[key];
	});
	return payload;
}

async function handleSubmit() {
	if (!allowSubmit.value || isLoading.value) return;

	isLoading.value = true;
	errors.value = [];

	try {
		if (props.emailExist) {
			// Existing account joining this hub → attach, persist fields, auto-login.
			const attachRes = await axios.post('/api/sso/api/user-attach', {
				email: user.email,
				subdomain: window.store.subdomain,
				logo: event.value.login_logo,
				'cf-turnstile-response': turnstileToken.value,
			});

			if (attachRes.data.errors) {
				errors.value = attachRes.data.errors;
				return;
			}
			if (!attachRes.data.success) {
				errors.value = ['Something went wrong, please try again later!'];
				return;
			}

			// Sent once they are signed in: /api/register takes the account from the token.
			stashRegistration(user.email, collectRegistrationFields());

			await finishExistingLogin();
		} else {
			// Brand-new account → keep the answers for after verification signs them in.
			stashRegistration(user.email, collectRegistrationFields());

			if (props.currentFormStepData.fromActivation) {
				// The invitee already set their password, so nothing has sent them a
				// code yet — activation owns that step.
				await finishActivation(user.email, props.changeFormStep, props.currentFormStepData);
			} else {
				props.changeFormStep('verify-code', { email: user.email });
			}
		}
	} catch (error) {
		errors.value = [error.response?.data?.message || 'Something went wrong, please try again later!'];
	} finally {
		isLoading.value = false;
	}
}

async function finishExistingLogin() {
	if (authStore.pendingTokenData) {
		const tokenData = authStore.pendingTokenData;
		authStore.setPendingTokenData(null);
		await authStore.retrieveUserData();
		authStore.login({ ...tokenData, role: authStore.userData?.role });
	}
	authStore.setUserAlreadyExistEmail(null);
	authStore.setPendingSsoUser(null);
	router.push('/');
}

function loadCaptcha() {
	const _env = window.location.hostname.split('.');
	if (!['qa', 'local'].includes(_env[1])) return;

	const script = document.createElement('script');
	script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
	script.async = true;
	script.onload = () => {
		if (document.querySelector('#turnstile-container')) {
			document.querySelector('#turnstile-container').innerHTML = '';
		}

		window.turnstile.render(document.querySelector('#turnstile-container'), {
			theme: 'light',
			appearance: 'interaction-only',
			sitekey: import.meta.env.VITE_TURNSTILE_SITEKEY,
			callback: function (token) {
				turnstileToken.value = token;
			},
		});
	};
	document.head.appendChild(script);
}

function validateForm(newVal) {
	let _allowSubmit = true;

	requiredFields.value.forEach(requiredField => {
		requiredField.forEach(field => {
			if (!newVal[`reg_${field.name}`]) _allowSubmit = false;

			if (field.type === 'custom') {
				if (newVal[`reg_${field.name}`]?.replace(/\s+/g, '').toLowerCase() === 'other') {
					if (!newVal[`reg_${field.name}_custom`]) _allowSubmit = false;
				}
			}
		});
	});

	allowSubmit.value = _allowSubmit;
}

watch(userFormData, newVal => {
	validateForm(newVal);
}, { deep: true });

onMounted(() => {
	loadCaptcha();

	// Nothing configured to collect → there is no form to put in front of anyone
	// and nothing to validate; the step only has to perform the join. The
	// template renders a loader instead, so a hub whose Registration form was
	// never customised doesn't flash an empty "Registration" panel for the
	// second or two the join takes before it redirects.
	if (!hasFields.value) {
		allowSubmit.value = true;
		handleSubmit();
		return;
	}

	validateForm(userFormData.value);
});
</script>

<template>
	<!-- Nothing to collect: no form, just the join in flight. A failure still has
	     to be reachable, so it replaces the loader rather than leaving a skeleton
	     that never resolves. -->
	<div class="form" v-if="!hasFields">
		<template v-if="errors.length">
			<div class="form__errors">
				<div v-for="error in errors" :key="error">{{ error }}</div>
			</div>
			<div class="form__actions">
				<pge-button label="Close" @click="() => router.push('/')" template="text" />
			</div>
		</template>
		<div v-else class="form__joining">
			<Loading variant="lines" :rows="2" />
		</div>
	</div>

	<div class="form" v-else>
		<div class="form__title">Registration</div>
		<div class="form-group" v-for="(group, groupIndex) in registerGroups" :key="groupIndex">
			<div class="form-group__title">
				{{ group.title }}
			</div>
			<div v-for="(field, fieldIndex) in group.fields" :key="fieldIndex">
				<template v-if="field.type === 'text'">
					<pge-input-text labelType="separated" maxLength="300" type="text" :label="field.title" :name="field.name"
						:model="userFormData[`reg_${field.name}`]" v-model="userFormData[`reg_${field.name}`]"
						:required="field.required" :has-required-sign="field.required" hasTrailingIcon />
				</template>
				<template v-if="field.type === 'country'">
					<form-dropdown labelType="separated" :model="userFormData[`reg_${field.name}`]"
						v-model="userFormData[`reg_${field.name}`]" :label="field.title" :name="field.name"
						:options="eventData.countries.map(item => ({ label: item, value: item }))" :required="field.required"
						:has-required-sign="field.required" />
				</template>
				<template v-if="field.type === 'state'">
					<form-dropdown labelType="separated"
						v-if="group.fields.find(item => item.type === 'country') && userFormData[`reg_${group.fields.find(item => item.type === 'country').name}`]"
						:model="userFormData[`reg_${field.name}`]" v-model="userFormData[`reg_${field.name}`]" :label="field.title"
						:disabled="userFormData[`reg_${group.fields.find(item => item.type === 'country').name}`] ? false : true"
						:name="field.name"
						:options="eventData.regions[userFormData[`reg_${group.fields.find(item => item.type === 'country').name}`]].map(item => ({ label: item, value: item }))"
						:required="field.required" :has-required-sign="field.required" />
					<form-dropdown-group v-if="!group.fields.find(item => item.type === 'country')" labelType="separated"
						:model="userFormData[`reg_${field.name}`]" v-model="userFormData[`reg_${field.name}`]" :label="field.title"
						:name="field.name" :options="Object.entries(eventData.regions).reduce((acc, [country, items]) => {
							acc[country] = items.map(item => ({ label: item, value: item }));
							return acc;
						}, {})" required />
				</template>
				<!--
					Policy type. The choices come from the hub's Policy lines and arrive
					in the same payload as the country and region lists, so the wording a
					stranger picks here is the wording on their record and in the segment
					built from it. No "Other" free-text branch: an answer that is not one
					of the hub's lines is not a policy it sells.
				-->
				<template v-if="field.type === 'policy_line'">
					<form-dropdown labelType="separated" :model="userFormData[`reg_${field.name}`]"
						v-model="userFormData[`reg_${field.name}`]" :label="field.title" :name="field.name"
						:required="field.required" :has-required-sign="field.required"
						:options="policyLineOptions" />
				</template>
				<template v-if="field.type === 'custom'">
					<form-dropdown labelType="separated" :model="userFormData[`reg_${field.name}`]"
						v-model="userFormData[`reg_${field.name}`]" :label="field.title" :name="field.name" :required="field.required"
						:has-required-sign="field.required"
						:options="field.options.split(',').map(item => ({ label: item, value: item }))" />
				</template>
				<div style="margin-top: 15px"
					v-if="!['text', 'policy_line'].includes(field.type) && userFormData[`reg_${field.name}`]?.replace(/\s+/g, '').toLowerCase() === 'other'">
					<pge-input-text labelType="separated" maxLength="300" type="text" :label="`Your ${field.title}`"
						:name="`${field.name}_custom`" :model="userFormData[`reg_${field.name}_custom`]"
						v-model="userFormData[`reg_${field.name}_custom`]" :required="field.required"
						:has-required-sign="field.required" hasTrailingIcon />
				</div>
			</div>
		</div>
		<div id="turnstile-container"></div>
		<div class="form__actions">
			<pge-button label="Close" @click="() => router.push('/')" template="text" />
			<pge-button :label="emailExist ? 'Join hub' : 'Register'" @click="handleSubmit"
				:disabled="!allowSubmit || isLoading" />
		</div>
		<div class="form__errors" v-if="errors.length">
			<div v-for="error in errors" :key="error">{{ error }}</div>
		</div>
	</div>
</template>

<style lang="scss" scoped>
.form {
	display: flex;
	flex-direction: column;
	gap: 22px;

	&__title {
		text-align: center;
		font-weight: 600;
		font-size: 18px;
		line-height: 1.5;
		color: #000;
	}

	.form-group {
		background-color: white;
		width: 100%;
		display: flex;
		flex-direction: column;
		gap: 15px;

		.form-group__title {
			color: #000;
			font-feature-settings: 'liga' off, 'clig' off;
			font-family: Lato, sans-serif;
			font-size: 16px;
			font-style: normal;
			font-weight: 700;
			line-height: normal;
		}
	}

	&__joining {
		height: 60px;
	}

	&__actions {
		display: flex;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}

	&__errors {
		display: flex;
		flex-direction: column;
		gap: 10px;

		div {
			text-align: center;
			color: #f83c3c;
			font-weight: 700;
			font-size: 14px;
			line-height: 20px;
		}
	}
}
</style>
